import React, { useRef, useState } from 'react';

const PDF_TYPES = ['application/pdf'];
const DOCX_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
];

interface UploadSectionProps {
  isUploading: boolean;
  isParsingDocx: boolean;
  uploadError: string | null;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>, allowedTypes: string[]) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, allowedTypes: string[]) => void;
  onStartEditing: () => void;
  onClearError: () => void;
  onDocxSelect: (file: File) => void;
}

const UploadSection: React.FC<UploadSectionProps> = ({
  isUploading,
  isParsingDocx,
  uploadError,
  onFileChange,
  onDrop,
  onStartEditing,
  onClearError,
  onDocxSelect,
}) => {
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const docxInputRef = useRef<HTMLInputElement>(null);
  const [tipsOpen, setTipsOpen] = useState(true);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileUpload = () => {
    pdfInputRef.current?.click();
  };

  const handleDocxUpload = () => {
    docxInputRef.current?.click();
  };

  const handleDocxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onDocxSelect(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-6">
      {/* Which option is right for you — collapsible decision guide */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setTipsOpen(o => !o)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-left hover:bg-gray-100 transition-colors"
        >
          <span className="text-sm font-medium text-gray-800">Which option is right for you?</span>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ${tipsOpen ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {tipsOpen && (
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">

            {/* Start Fresh */}
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                  <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-gray-800">Start Fresh</p>
              </div>
              <p className="text-xs text-gray-500">Best for authors starting a new book directly in IndiePress.</p>
              <ul className="space-y-1.5">
                {[
                  { yes: true,  text: 'Full editor with formatting tools' },
                  { yes: true,  text: 'Add chapters as you write' },
                  { yes: true,  text: 'Auto-save every 2 minutes' },
                  { yes: false, text: 'No existing manuscript needed' },
                ].map((item, i) => (
                  <li key={i} className="flex gap-2 items-start">
                    <span className={`text-xs shrink-0 mt-0.5 font-semibold ${item.yes ? 'text-green-500' : 'text-gray-300'}`}>
                      {item.yes ? '✓' : '○'}
                    </span>
                    <span className="text-xs text-gray-600">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Import DOCX */}
            <div className="p-4 space-y-3 bg-orange-50/40">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                  <svg className="w-3.5 h-3.5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-gray-800">Import DOCX</p>
              </div>
              <p className="text-xs text-gray-500">Best for authors with an existing Word manuscript ready to publish.</p>
              <ul className="space-y-1.5">
                {[
                  { yes: true,  text: 'Chapters auto-detected from Heading styles' },
                  { yes: true,  text: 'Images and tables carried over' },
                  { yes: true,  text: 'Review and rename chapters before import' },
                  { yes: false, text: 'Chapter titles must use Heading 1 or 2 style in Word — just bold texts won\'t work' },
                ].map((item, i) => (
                  <li key={i} className="flex gap-2 items-start">
                    <span className={`text-xs shrink-0 mt-0.5 font-semibold ${item.yes ? 'text-green-500' : 'text-amber-400'}`}>
                      {item.yes ? '✓' : '!'}
                    </span>
                    <span className="text-xs text-gray-600">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Upload PDF */}
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                  <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-gray-800">Upload PDF</p>
              </div>
              <p className="text-xs text-gray-500">Best for authors with a fully formatted, print-ready PDF.</p>
              <ul className="space-y-1.5">
                {[
                  { yes: true,  text: 'Fastest path to submission' },
                  { yes: true,  text: 'Spine width auto-calculated from page count' },
                  { yes: false, text: 'Content is not editable after upload' },
                  { yes: false, text: 'Formatting must meet print specifications' },
                ].map((item, i) => (
                  <li key={i} className="flex gap-2 items-start">
                    <span className={`text-xs shrink-0 mt-0.5 font-semibold ${item.yes ? 'text-green-500' : 'text-gray-300'}`}>
                      {item.yes ? '✓' : '○'}
                    </span>
                    <span className="text-xs text-gray-600">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        )}
      </div>

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={pdfInputRef}
        onChange={(e) => onFileChange(e, PDF_TYPES)}
        accept=".pdf"
        className="hidden"
      />
      <input
        type="file"
        ref={docxInputRef}
        onChange={handleDocxChange}
        accept=".docx,.doc"
        className="hidden"
      />

      {/* Upload Error Banner */}
      {uploadError && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
          <div className="flex justify-between items-start">
            <p className="text-red-700 text-sm">{uploadError}</p>
            <button
              onClick={onClearError}
              className="text-red-600 hover:text-red-800 text-sm underline ml-4"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Upload Options Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Start Editing Section */}
        <div>
          <label className="block text-gray-900 text-sm font-medium mb-3">
            Start adding content using our editing tool :
          </label>
          <p className="text-gray-500 text-xs mb-3">
            Use our internal editor to write and format your book
          </p>
          <div className="border-2 border-dashed rounded-lg p-8 text-center transition-colors bg-gray-50 border-gray-300 hover:border-gray-400">
            <div className="text-gray-400 text-5xl mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <p className="text-gray-600 text-sm mb-4">Write your book content</p>
            <button
              onClick={onStartEditing}
              className="bg-gray-900 text-white px-6 py-2.5 rounded-md hover:bg-gray-800 transition-colors font-medium text-sm"
            >
              Start Editing
            </button>
          </div>
        </div>

        {/* Import DOCX */}
        <div>
          <label className="block text-gray-900 text-sm font-medium mb-3">
            Import existing manuscript :
          </label>
          <p className="text-gray-500 text-xs mb-3">
            Chapters are auto-detected from styles in your Word document
          </p>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors bg-gray-50 ${
              isParsingDocx ? 'border-orange-400 bg-orange-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const file = e.dataTransfer.files?.[0];
              if (file && DOCX_TYPES.includes(file.type)) onDocxSelect(file);
            }}
            onDragOver={handleDragOver}
          >
            {isParsingDocx ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                <p className="text-orange-600 text-sm">Reading document…</p>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-600 text-sm mb-4">Drag .docx here or click to browse</p>
                <button
                  onClick={handleDocxUpload}
                  className="bg-gray-900 text-white px-6 py-2.5 rounded-md hover:bg-gray-800 transition-colors font-medium text-sm"
                >
                  Import DOCX
                </button>
              </>
            )}
          </div>
        </div>

        {/* Upload PDF */}
        <div>
          <label className="block text-gray-900 text-sm font-medium mb-3">
            Upload a print ready PDF :
          </label>
          <p className="text-gray-500 text-xs mb-3">
            (note: Print ready PDF will not be editable)
          </p>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors bg-gray-50 ${
              isUploading ? 'border-orange-400 bg-orange-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            onDrop={(e) => onDrop(e, PDF_TYPES)}
            onDragOver={handleDragOver}
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                <p className="text-orange-600 text-sm">Uploading...</p>
              </>
            ) : (
              <>
                <div className="text-gray-400 text-5xl mb-4">+</div>
                <p className="text-gray-600 text-sm mb-4">Drag here to upload</p>
                <button
                  onClick={handleFileUpload}
                  className="bg-gray-900 text-white px-6 py-2.5 rounded-md hover:bg-gray-800 transition-colors font-medium text-sm"
                >
                  Upload
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Banners */}
      <div className="md:hidden bg-orange-50 border-l-4 border-orange-500 p-4 rounded mt-6">
        <p className="text-orange-800 text-sm font-medium mb-2">
          Book formatting not uploaded
        </p>
        <p className="text-orange-700 text-xs">
          To use our book formatting tool kindly, using a laptop/desktop
        </p>
      </div>

    </div>
  );
};

export default UploadSection;
